import { observer } from "mobx-react-lite";
import { useTran, useUnsavedChanges } from "@/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader, UnsavedChangesDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Building2, Paperclip } from "lucide-react";
import { PATHS } from "@/lib/router";
import { FormField, SelectField, SearchableSelect } from "@/components/common";
import {
  formatYear,
  generateYearOptions,
} from "@/features/esg/utils/year-helpers";
import { useState, useEffect } from "react";
import { useAuthStore } from "@/features/auth";
import { useCarbonSurveysStore } from "../hooks";
import EvidenceUpload from "../components/EvidenceUpload";

const CarbonSurveysFormPage = () => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    handleSaveCarbonInventory,
    handleChange,
    carbonSurveysData,
    hasDataChanges,
    getCarbonSurveysDataById,
    handleClear,
  } = useCarbonSurveysStore();

  const { showDialog, confirmDiscard, cancelDiscard, bypass } =
    useUnsavedChanges(hasDataChanges);

  const yearOptions = generateYearOptions(2019).map((year) => ({
    value: String(year),
    label: formatYear(year),
  }));
  const bankStatus = carbonSurveysData?.bankReview?.status === "approved";

  const statuses = [
    { title: "We know our total", key: "total" },
    { title: "We dont know(enter activities)", key: "activities" },
    { title: "Not Set", key: "not_set" },
  ];

  const unitOptions = [
    { key: "kgCO2e", label: "kgCO₂e" },
    { key: "tCO2e", label: "tCO₂e" },
  ];

  const handleSave = async () => {
    const result = await handleSaveCarbonInventory();
    if (!result) return;
    bypass();
    navigate(PATHS.CARBON_SURVEYS);
  };

  useEffect(() => {
    if (id) {
      getCarbonSurveysDataById(id);
    } else {
      handleChange(user.companyName, "companyName", undefined, {
        silent: true,
      });
    }
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          isEdit
            ? t("Edit Carbon Inventory Survey")
            : t("Add Carbon Inventory Survey")
        }
        description={t(
          "If you don't know totals, enter activity data and attach bills/logs next to each input.",
        )}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                navigate(PATHS.CARBON_SURVEYS);
                handleClear();
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("Back")}
            </Button>
            <Button onClick={handleSave} disabled={!hasDataChanges}>
              {/* <PlusCircle /> */}
              {isEdit ? t("Update") : t("Save")}
            </Button>
            <Button
              disabled={carbonSurveysData.status === "submitted" ? true : false}
              onClick={() => {
                handleChange("submitted", "status");
                handleSave();
              }}
            >
              {"Submit"}
            </Button>
          </div>
        }
      />
      <div className="flex h-full w-full flex-col rounded-radius border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="text-base font-semibold text-foreground">
            {t("General Information")}
          </p>
        </div>
        <div className="px-4 py-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="">
            <div className="mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <label className="text-sm font-medium text-foreground">
                {t("Company Name")}
              </label>
            </div>
            <FormField
              type="string"
              value={carbonSurveysData?.companyName || ""}
              onChange={(e) => handleChange(e.target.value, "companyName")}
              placeholder={t("Enter company name")}
              disabled={true}
              // error={errors?.value}
            />
          </div>
          <div className="">
            <div className="mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <label className="text-sm font-medium text-foreground">
                {t("Reporting Year")}
              </label>
            </div>
            <SearchableSelect
              value={String(carbonSurveysData?.reportingYear)}
              onChange={(value) => handleChange(Number(value), "reportingYear")}
              options={yearOptions}
              placeholder={t("Select year")}
              searchPlaceholder={t("Search year")}
              emptyText={t("No year available")}
            />
          </div>
        </div>

        <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
          <div className="items-center justify-between border-border px-4 py-3">
            <p className="text-sm font-medium text-foreground">
              {t("Scope 1 - Direct Emission")}
            </p>
            <div className="flex gap-3 py-2">
              {statuses.map((status) => {
                const isActive =
                  status.key === carbonSurveysData?.data?.scope1?.mode;
                return (
                  <Button
                    variant={isActive ? "default" : "outline"}
                    key={status.key}
                    id={status.key}
                    size="sm"
                    onClick={() => handleChange(status.key, "scope1", "mode")}
                  >
                    {t(status.title)}
                  </Button>
                );
              })}
            </div>
          </div>
          {carbonSurveysData?.data?.scope1?.mode === "total" ? (
            <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
              <div className="items-center justify-between border-border px-4 py-3">
                <p className="text-sm font-medium text-foreground pb-3">
                  {t("Total Scope 1 emissions")}
                </p>

                <div className="flex gap-3">
                  <div className="w-2/5">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="text-sm font-medium text-foreground">
                        {t("Annual value")}
                      </label>
                    </div>
                    <FormField
                      type="number"
                      value={
                        carbonSurveysData["data"]["scope1"]["totalco2e"] || ""
                      }
                      onChange={(e) =>
                        handleChange(
                          Number(e.target.value),
                          "scope1",
                          "totalco2e",
                        )
                      }
                      placeholder={t("0")}
                      // disabled={disabled}
                      // error={errors?.value}
                    />
                  </div>

                  <div className="w-1/5">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="text-sm font-medium text-foreground">
                        {t("Unit")}
                      </label>
                    </div>
                    <SelectField
                      options={unitOptions}
                      value={String(
                        carbonSurveysData["data"]["scope1"]["totalco2eUnit"] ||
                          "",
                      )}
                      onChange={(e) => {
                        handleChange(e.target.value, "scope1", "totalco2eUnit");
                      }}
                      placeholder={t("")}
                      // disabled={disabled}
                      // error={errors?.value}
                    />
                  </div>

                  <div className="w-2/5">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="text-sm font-medium text-foreground">
                        {t("Evidence (annual summary)")}
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <EvidenceUpload
                        scope={"scope1"}
                        mode={"total"}
                        isEdit={isEdit}
                        proofs={
                          carbonSurveysData["data"]?.["scope1"]?.[
                            "totalEvidenceFiles"
                          ] || []
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : carbonSurveysData?.data?.scope1?.mode === "activities" ? (
            <>
              <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
                <div className="items-center justify-between border-border px-4 py-3">
                  <div className="flex justify-between">
                    <p className="text-sm font-medium text-foreground pb-3">
                      {t("Stationary combustion (diesel, gas, LPG)")}
                    </p>
                    {/* <SelectField
                      className="w-1/5"
                      options={periods}
                      value={String(carbonSurveysData["data"]["scope1"]["activities"]["stationary"]["period"] || "")}
                      onChange={(e) => {
                        handleChange(e.target.value, "stationary", "period");
                      }}
                      placeholder={t("")}
                    // disabled={disabled}
                    // error={errors?.value}
                    /> */}
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Annual value")}
                        </label>
                      </div>
                      <FormField
                        type="number"
                        value={
                          carbonSurveysData["data"]["scope1"]["activities"][
                            "stationary"
                          ]["entries"]?.[0]?.["quantity"] || ""
                        }
                        onChange={(e) => {
                          handleChange(
                            e.target.value,
                            "stationary",
                            "quantity",
                          );
                        }}
                        placeholder={t("0")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-1/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Unit")}
                        </label>
                      </div>
                      <SelectField
                        options={unitOptions}
                        value={
                          carbonSurveysData["data"]["scope1"]["activities"][
                            "stationary"
                          ]["entries"]?.[0]?.["unit"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "stationary", "unit");
                        }}
                        placeholder={t("")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Evidence (annual summary)")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <EvidenceUpload
                          scope={"stationary"}
                          isEdit={isEdit}
                          mode={"activities"}
                          proofs={
                            carbonSurveysData["data"]?.["scope1"]?.[
                              "activities"
                            ]?.["stationary"]?.["entries"]?.[0]?.[
                              "evidenceFiles"
                            ] || []
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
                <div className="items-center justify-between border-border px-4 py-3">
                  <p className="text-sm font-medium text-foreground pb-3">
                    {t("Mobile combustion (company vehicles, forklifts)")}
                  </p>
                  <div className="flex gap-3">
                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Annual value")}
                        </label>
                      </div>
                      <FormField
                        type="number"
                        value={
                          carbonSurveysData["data"]["scope1"]["activities"][
                            "mobile"
                          ]["entries"]?.[0]?.["quantity"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "mobile", "quantity");
                        }}
                        placeholder={t("0")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-1/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Unit")}
                        </label>
                      </div>
                      <SelectField
                        options={unitOptions}
                        value={
                          carbonSurveysData["data"]["scope1"]["activities"][
                            "mobile"
                          ]["entries"]?.[0]?.["unit"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "mobile", "unit");
                        }}
                        placeholder={t("")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Evidence (annual summary)")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <EvidenceUpload
                          scope={"mobile"}
                          mode={"activities"}
                          isEdit={isEdit}
                          proofs={
                            carbonSurveysData["data"]?.["scope1"]?.[
                              "activities"
                            ]?.["mobile"]?.["entries"]?.[0]?.[
                              "evidenceFiles"
                            ] || []
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
                <div className="items-center justify-between border-border px-4 py-3">
                  <p className="text-sm font-medium text-foreground pb-3">
                    {t("Fugitive emissions (refrigerants/AC leaks)")}
                  </p>
                  <div className="flex gap-3">
                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Annual value")}
                        </label>
                      </div>
                      <FormField
                        type="number"
                        value={
                          carbonSurveysData["data"]["scope1"]["activities"][
                            "fugitive"
                          ]["entries"]?.[0]?.["quantity"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "fugitive", "quantity");
                        }}
                        placeholder={t("0")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-1/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Unit")}
                        </label>
                      </div>
                      <SelectField
                        options={unitOptions}
                        value={
                          carbonSurveysData["data"]["scope1"]["activities"][
                            "fugitive"
                          ]["entries"]?.[0]?.["unit"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "fugitive", "unit");
                        }}
                        placeholder={t("")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Evidence (annual summary)")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <EvidenceUpload
                          scope={"fugitive"}
                          mode={"activities"}
                          isEdit={isEdit}
                          proofs={
                            carbonSurveysData["data"]?.["scope1"]?.[
                              "activities"
                            ]?.["fugitive"]?.["entries"]?.[0]?.[
                              "evidenceFiles"
                            ] || []
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
                <div className="items-center justify-between border-border px-4 py-3">
                  <p className="text-sm font-medium text-foreground pb-3">
                    {t("Process emissions (industrial gases)")}
                  </p>
                  <div className="flex gap-3">
                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Annual value")}
                        </label>
                      </div>
                      <FormField
                        type="number"
                        value={
                          carbonSurveysData["data"]["scope1"]["activities"][
                            "process"
                          ]["entries"]?.[0]?.["quantity"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "process", "quantity");
                        }}
                        placeholder={t("0")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-1/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Unit")}
                        </label>
                      </div>
                      <SelectField
                        options={unitOptions}
                        value={
                          carbonSurveysData["data"]["scope1"]["activities"][
                            "process"
                          ]["entries"]?.[0]?.["unit"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "process", "unit");
                        }}
                        placeholder={t("")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Evidence (annual summary)")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <EvidenceUpload
                          scope={"process"}
                          mode={"activities"}
                          isEdit={isEdit}
                          proofs={
                            carbonSurveysData["data"]?.["scope1"]?.[
                              "activities"
                            ]?.["process"]?.["entries"]?.[0]?.[
                              "evidenceFiles"
                            ] || []
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
              <div className="items-center justify-between border-border px-4 py-3">
                <p className="text-sm font-medium text-foreground">
                  {t("Scope 1 not set.")}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
          <div className="items-center justify-between border-border px-4 py-3">
            <p className="text-sm font-medium text-foreground pb-3">
              {t("Scope 2 - Purchased Energy")}
            </p>
            <div className="flex gap-3 py-2">
              {statuses.map((status) => {
                const isActive =
                  status.key === carbonSurveysData?.data?.scope2?.mode;
                return (
                  <Button
                    variant={isActive ? "default" : "outline"}
                    key={status.key}
                    id={status.key}
                    size="sm"
                    onClick={() => handleChange(status.key, "scope2", "mode")}
                  >
                    {t(status.title)}
                  </Button>
                );
              })}
            </div>
          </div>
          {carbonSurveysData?.data?.scope2?.mode === "total" ? (
            <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
              <div className="items-center justify-between border-border px-4 py-3">
                <p className="text-sm font-medium text-foreground pb-3">
                  {t("Total Scope 2 emissions")}
                </p>
                <div className="flex gap-3">
                  <div className="w-2/5">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="text-sm font-medium text-foreground">
                        {t("Annual value")}
                      </label>
                    </div>
                    <FormField
                      type="number"
                      value={
                        carbonSurveysData["data"]["scope2"]["totalco2e"] || ""
                      }
                      onChange={(e) =>
                        handleChange(
                          Number(e.target.value),
                          "scope2",
                          "totalco2e",
                        )
                      }
                      placeholder={t("0")}
                      // disabled={disabled}
                      // error={errors?.value}
                    />
                  </div>

                  <div className="w-1/5">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="text-sm font-medium text-foreground">
                        {t("Unit")}
                      </label>
                    </div>
                    <SelectField
                      options={unitOptions}
                      value={String(
                        carbonSurveysData["data"]["scope2"]["totalco2eUnit"] ||
                          "",
                      )}
                      onChange={(e) => {
                        handleChange(e.target.value, "scope2", "totalco2eUnit");
                      }}
                      placeholder={t("")}
                      // disabled={disabled}
                      // error={errors?.value}
                    />
                  </div>

                  <div className="w-2/5">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="text-sm font-medium text-foreground">
                        {t("Evidence (annual summary)")}
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <EvidenceUpload
                        scope={"scope2"}
                        mode={"total"}
                        isEdit={isEdit}
                        proofs={
                          carbonSurveysData["data"]?.["scope2"]?.[
                            "totalEvidenceFiles"
                          ] || []
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : carbonSurveysData?.data?.scope2?.mode === "activities" ? (
            <>
              <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
                <div className="items-center justify-between border-border px-4 py-3">
                  <p className="text-sm font-medium text-foreground pb-3">
                    {t("Purchased electricity (grid or renewable)")}
                  </p>
                  <div className="flex gap-3">
                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Annual value")}
                        </label>
                      </div>
                      <FormField
                        type="number"
                        value={
                          carbonSurveysData["data"]["scope2"]["activities"][
                            "electricity"
                          ]["entries"]?.[0]?.["quantity"] || ""
                        }
                        onChange={(e) => {
                          handleChange(
                            e.target.value,
                            "electricity",
                            "quantity",
                          );
                        }}
                        placeholder={t("0")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-1/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Unit")}
                        </label>
                      </div>
                      <SelectField
                        options={unitOptions}
                        value={
                          carbonSurveysData["data"]["scope2"]["activities"][
                            "electricity"
                          ]["entries"]?.[0]?.["unit"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "electricity", "unit");
                        }}
                        // placeholder={t("")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Evidence (annual summary)")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <EvidenceUpload
                          scope={"electricity"}
                          mode={"activities"}
                          isEdit={isEdit}
                          proofs={
                            carbonSurveysData["data"]?.["scope2"]?.[
                              "activities"
                            ]?.["electricity"]?.["entries"]?.[0]?.[
                              "evidenceFiles"
                            ] || []
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
                <div className="items-center justify-between border-border px-4 py-3">
                  <p className="text-sm font-medium text-foreground pb-3">
                    {t("Purchased steam/heat")}
                  </p>
                  <div className="flex gap-3">
                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Annual value")}
                        </label>
                      </div>
                      <FormField
                        type="number"
                        value={
                          carbonSurveysData["data"]["scope2"]["activities"][
                            "steam"
                          ]["entries"]?.[0]?.["quantity"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "steam", "quantity");
                        }}
                        placeholder={t("0")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-1/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Unit")}
                        </label>
                      </div>
                      <SelectField
                        options={unitOptions}
                        value={
                          carbonSurveysData["data"]["scope2"]["activities"][
                            "steam"
                          ]["entries"]?.[0]?.["unit"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "steam", "unit");
                        }}
                        placeholder={t("")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Evidence (annual summary)")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <EvidenceUpload
                          scope={"steam"}
                          mode={"activities"}
                          isEdit={isEdit}
                          proofs={
                            carbonSurveysData["data"]?.["scope2"]?.[
                              "activities"
                            ]?.["steam"]?.["entries"]?.[0]?.["evidenceFiles"] ||
                            []
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
                <div className="items-center justify-between border-border px-4 py-3">
                  <p className="text-sm font-medium text-foreground pb-3">
                    {t("Purchased cooling or chilled water")}
                  </p>
                  <div className="flex gap-3">
                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Annual value")}
                        </label>
                      </div>
                      <FormField
                        type="number"
                        value={
                          carbonSurveysData["data"]["scope2"]["activities"][
                            "cooling"
                          ]["entries"]?.[0]?.["quantity"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "cooling", "quantity");
                        }}
                        placeholder={t("0")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-1/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Unit")}
                        </label>
                      </div>
                      <SelectField
                        options={unitOptions}
                        value={
                          carbonSurveysData["data"]["scope2"]["activities"][
                            "cooling"
                          ]["entries"]?.[0]?.["unit"] || ""
                        }
                        onChange={(e) => {
                          handleChange(e.target.value, "cooling", "unit");
                        }}
                        placeholder={t("")}
                        // disabled={disabled}
                        // error={errors?.value}
                      />
                    </div>

                    <div className="w-2/5">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">
                          {t("Evidence (annual summary)")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <EvidenceUpload
                          scope={"cooling"}
                          mode={"activities"}
                          isEdit={isEdit}
                          proofs={
                            carbonSurveysData["data"]?.["scope2"]?.[
                              "activities"
                            ]?.["cooling"]?.["entries"]?.[0]?.[
                              "evidenceFiles"
                            ] || []
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="mx-4 my-3 flex-col rounded-radius border border-border bg-card shadow-sm">
              <div className="items-center justify-between border-border px-4 py-3">
                <p className="text-sm font-medium text-foreground pb-3">
                  {t("Scope 2 not set.")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </div>
  );
};
export default observer(CarbonSurveysFormPage);
